/**
 * US Data Subject Request (DSR) Service (US-159 / T-159-001)
 *
 * Client for the CCPA/CPRA DSR backend endpoints:
 *   POST /api/texas/ccpa/dsr/requests         → submit new DSR
 *   GET  /api/texas/ccpa/dsr/requests         → list all DSRs
 *   GET  /api/texas/ccpa/dsr/requests/:id     → get single DSR
 *   POST /api/texas/ccpa/dsr/requests/:id/verify  → verify subject identity
 *   POST /api/texas/ccpa/dsr/requests/:id/extend  → request deadline extension
 *   POST /api/texas/ccpa/dsr/requests/:id/fulfill → fulfill/complete the DSR
 *   GET  /api/texas/ccpa/dsr/requests/:id/audit   → get audit trail
 *   GET  /api/texas/ccpa/dsr/deadlines/sweep      → get upcoming deadlines
 *
 * Envelope: { success, data }. Unwraps responses before returning.
 */

import { landlordApi } from '../../services/api';

export type DSRType = 'ACCESS' | 'DELETE' | 'OPT_OUT';

export type DSRStatus =
  | 'PENDING'
  | 'VERIFIED'
  | 'IN_PROGRESS'
  | 'EXTENDED'
  | 'FULFILLED'
  | 'REJECTED';

export interface AuditEntry {
  action: string;
  actor: string;
  timestamp: string;
  details?: string;
}

export interface DSRequest {
  id: string;
  type: DSRType;
  status: DSRStatus;
  subjectEmail: string;
  subjectName: string;
  details: string;
  createdAt: string;
  deadline: string;
  verifiedAt?: string;
  fulfilledAt?: string;
  auditTrail: AuditEntry[];
}

export interface CreateDSRInput {
  type: DSRType;
  subjectEmail: string;
  subjectName: string;
  details: string;
}

export interface VerifyDSRInput {
  verified?: boolean;
  notes?: string;
  verifiedBy?: string;
  [key: string]: unknown;
}

export interface ExtendDSRInput {
  days?: number;
  reason?: string;
  extendedBy?: string;
  [key: string]: unknown;
}

export interface FulfillDSRInput {
  notes?: string;
  fulfilledBy?: string;
  fulfillmentData?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface DeadlineSweepItem {
  id: string;
  type: DSRType;
  status: DSRStatus;
  subjectEmail: string;
  deadline: string;
  daysRemaining: number;
  isOverdue: boolean;
  [key: string]: unknown;
}

export interface DeadlineSweepResult {
  asOf?: string;
  upcomingCount?: number;
  overdueCount?: number;
  deadlines?: DeadlineSweepItem[];
  requests?: DSRequest[];
  [key: string]: unknown;
}

/**
 * Unwrap backend { success, data } envelope safely.
 */
export function unwrapEnvelope<T>(res: any): T {
  if (res && typeof res === 'object') {
    if (res.data !== undefined) {
      const inner = res.data;
      if (inner && typeof inner === 'object') {
        if (inner.data !== undefined) {
          return inner.data as T;
        }
        return inner as T;
      }
      return inner as T;
    }
    if (res.success !== undefined && res.data !== undefined) {
      return res.data as T;
    }
  }
  return res as T;
}

/**
 * Calculate the number of whole days remaining until a statutory deadline.
 */
export function calculateDaysRemaining(deadline: string | number | Date): number {
  const target = new Date(deadline).getTime();
  const now = Date.now();
  const diffMs = target - now;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Check if a statutory deadline is overdue.
 */
export function isOverdue(deadline: string | number | Date): boolean {
  return calculateDaysRemaining(deadline) < 0;
}

/**
 * Format deadline remaining days into a human-readable countdown string.
 */
export function formatDeadlineCountdown(deadline: string | number | Date): string {
  const days = calculateDaysRemaining(deadline);
  if (days < 0) {
    const overdueDays = Math.abs(days);
    return `${overdueDays} day${overdueDays === 1 ? '' : 's'} overdue`;
  }
  if (days === 0) {
    return 'Due today';
  }
  return `${days} day${days === 1 ? '' : 's'} remaining`;
}

/**
 * DSR API client service.
 */
export const dsrService = {
  /**
   * Submit a new DSR.
   * POST /api/texas/ccpa/dsr/requests
   */
  submitRequest: async (input: CreateDSRInput): Promise<DSRequest> => {
    const res = await landlordApi.post('/api/texas/ccpa/dsr/requests', input);
    return unwrapEnvelope<DSRequest>(res);
  },

  /**
   * Alias for submitRequest.
   */
  createRequest: async (input: CreateDSRInput): Promise<DSRequest> => {
    return dsrService.submitRequest(input);
  },

  /**
   * List all DSRs, optionally filtered by status.
   * GET /api/texas/ccpa/dsr/requests
   */
  listRequests: async (status?: DSRStatus | string): Promise<DSRequest[]> => {
    const url = status && status !== 'ALL'
      ? `/api/texas/ccpa/dsr/requests?status=${encodeURIComponent(status)}`
      : '/api/texas/ccpa/dsr/requests';
    const res = await landlordApi.get(url);
    const data = unwrapEnvelope<any>(res);
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.requests)) return data.requests;
    return [];
  },

  /**
   * Get single DSR by ID.
   * GET /api/texas/ccpa/dsr/requests/:requestId
   */
  getRequest: async (requestId: string): Promise<DSRequest> => {
    const res = await landlordApi.get(`/api/texas/ccpa/dsr/requests/${encodeURIComponent(requestId)}`);
    return unwrapEnvelope<DSRequest>(res);
  },

  /**
   * Verify subject identity for a DSR.
   * POST /api/texas/ccpa/dsr/requests/:requestId/verify
   */
  verifyRequest: async (requestId: string, payload: VerifyDSRInput = {}): Promise<DSRequest> => {
    const res = await landlordApi.post(
      `/api/texas/ccpa/dsr/requests/${encodeURIComponent(requestId)}/verify`,
      payload
    );
    return unwrapEnvelope<DSRequest>(res);
  },

  /**
   * Request statutory deadline extension (e.g. 45 days).
   * POST /api/texas/ccpa/dsr/requests/:requestId/extend
   */
  extendRequest: async (requestId: string, payload: ExtendDSRInput = {}): Promise<DSRequest> => {
    const res = await landlordApi.post(
      `/api/texas/ccpa/dsr/requests/${encodeURIComponent(requestId)}/extend`,
      payload
    );
    return unwrapEnvelope<DSRequest>(res);
  },

  /**
   * Fulfill/complete the DSR.
   * POST /api/texas/ccpa/dsr/requests/:requestId/fulfill
   */
  fulfillRequest: async (requestId: string, payload: FulfillDSRInput = {}): Promise<DSRequest> => {
    const res = await landlordApi.post(
      `/api/texas/ccpa/dsr/requests/${encodeURIComponent(requestId)}/fulfill`,
      payload
    );
    return unwrapEnvelope<DSRequest>(res);
  },

  /**
   * Get audit trail for a DSR.
   * GET /api/texas/ccpa/dsr/requests/:requestId/audit
   */
  getAuditTrail: async (requestId: string): Promise<AuditEntry[]> => {
    const res = await landlordApi.get(`/api/texas/ccpa/dsr/requests/${encodeURIComponent(requestId)}/audit`);
    const data = unwrapEnvelope<any>(res);
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.auditTrail)) return data.auditTrail;
    return [];
  },

  /**
   * Get upcoming deadlines sweep.
   * GET /api/texas/ccpa/dsr/deadlines/sweep
   */
  getUpcomingDeadlines: async (targetDate?: string): Promise<DeadlineSweepResult> => {
    const url = targetDate
      ? `/api/texas/ccpa/dsr/deadlines/sweep?date=${encodeURIComponent(targetDate)}`
      : '/api/texas/ccpa/dsr/deadlines/sweep';
    const res = await landlordApi.get(url);
    return unwrapEnvelope<DeadlineSweepResult>(res);
  },

  /**
   * Alias for getUpcomingDeadlines.
   */
  sweepDeadlines: async (targetDate?: string): Promise<DeadlineSweepResult> => {
    return dsrService.getUpcomingDeadlines(targetDate);
  },
};

export default dsrService;
